import { useLocalStorage } from '@/composables/useLocalStorage';
import { useLogoutRedirect } from '@/composables/useLogoutRedirect.ts';

const storage = useLocalStorage();
const setLS = storage.setLS;
const getLS = storage.getLS;
const removeLS = key => storage.setLS(key, undefined);

const MFA_KEY = 'auth._mfa_challenge';
const MFA_TYPE = 'auth._mfa_type';

let pendingFetchUser = null;

export const auth = {
  state: () => ({
    accessToken: '',
    isPasswordExpired: false,
    refreshToken: '',
    successAuthorizationReturnData: null,
    tokenType: 'bearer',
    twoFactorAuthorization: null,
    user: null,
    isLoggedIn: false,
    isBusy: false,
    hasMfaChallenge: !!getLS(MFA_KEY),
    mfaToken: '',
    isSelfReactivated: false
  }),
  getters: {
    isLoggedIn(state) {
      return state.isLoggedIn;
    },
    user(state) {
      return state.user;
    },
    accessToken(state) {
      return state.accessToken;
    },
    refreshToken(state) {
      return state.refreshToken;
    },
    twoFactorAuthorization(state) {
      return state.twoFactorAuthorization;
    },
    hasSecondFactorChallenge(state) {
      return !!state.twoFactorAuthorization || state.hasMfaChallenge;
    },
    twoFactorTypeId(state) {
      if (
        state.twoFactorAuthorization &&
        typeof state.twoFactorAuthorization.type !== 'undefined'
      ) {
        return parseInt(state.twoFactorAuthorization.type);
      }
      if (state.user && typeof state.user.twoFactorAuthorizationTypeId !== 'undefined') {
        return state.user.twoFactorAuthorizationTypeId;
      }
      return null;
    },
    mfaToken(state) {
      return state.mfaToken;
    },
    isSelfReactivated(state) {
      return state.isSelfReactivated;
    },
    isBusy(state) {
      return state.isBusy;
    }
  },
  mutations: {
    setAccessToken(state, accessToken) {
      state.accessToken = accessToken;
    },
    setMfaToken(state, mfaToken) {
      state.mfaToken = mfaToken;
      setLS('mfa._token', mfaToken);
    },
    setIsPasswordExpired(state, isPasswordExpired) {
      state.isPasswordExpired = isPasswordExpired;
    },
    setRefreshToken(state, refreshToken) {
      state.refreshToken = refreshToken;
    },
    setSuccessAuthorizationReturnData(state, successAuthorizationReturnData) {
      state.successAuthorizationReturnData = successAuthorizationReturnData;
    },
    setTokenType(state, tokenType) {
      state.tokenType = tokenType;
    },
    setTwoFactorAuthorization(state, twoFactorAuthorization) {
      state.twoFactorAuthorization = twoFactorAuthorization;
      const challenging = !!twoFactorAuthorization && twoFactorAuthorization.enabled;
      state.hasMfaChallenge = challenging;
      if (challenging) {
        setLS(MFA_KEY, '1');
        setLS(MFA_TYPE, twoFactorAuthorization.type);
      } else {
        removeLS(MFA_KEY);
        removeLS(MFA_TYPE);
      }
    },
    clearMfaChallenge(state) {
      state.hasMfaChallenge = false;
      removeLS(MFA_KEY);
      removeLS(MFA_TYPE);
    },
    setUser(state, user) {
      state.user = user;
    },
    setIsLoggedIn(state, isLoggedIn) {
      state.isLoggedIn = isLoggedIn;
    },
    setIsBusy(state, isBusy) {
      state.isBusy = isBusy;
    },
    setIsSelfReactivated(state, isSelfReactivated) {
      state.isSelfReactivated = isSelfReactivated;
    }
  },
  actions: {
    async setUserToken({ commit }, { bearerToken, refreshToken }) {
      commit('setAccessToken', bearerToken);
      commit('setRefreshToken', refreshToken);
      commit('general/setSendAuthorizationHeader', true, { root: true });
      commit('setIsLoggedIn', true);

      const { getTokenService } = await import('@/services/token');
      getTokenService().setMobileTokens(bearerToken, refreshToken);
    },

    async startMfaChallenge({ commit }, payload) {
      commit('setTwoFactorAuthorization', payload.twoFactorAuthorization);
      commit('setIsLoggedIn', false);
      commit('setMfaToken', payload.accessToken);
      commit('general/setSendAuthorizationHeader', false, { root: true });
    },

    async mfaSuccess({ dispatch, commit }) {
      commit('setIsLoggedIn', true);
      commit('general/setSendAuthorizationHeader', true, { root: true });
      await dispatch('fetchUser', true);
      commit('setTwoFactorAuthorization', null);
      commit('clearMfaChallenge');
      commit('setMfaToken', undefined);
      await dispatch('postLoginHydrate');
    },

    async loginSuccess({ commit, dispatch }, data) {
      commit('setAccessToken', data.accessToken);
      commit('setIsPasswordExpired', data.isPasswordExpired);
      commit('setRefreshToken', data.refreshToken);
      commit('setSuccessAuthorizationReturnData', data.successAuthorizationReturnData);
      commit('setTokenType', data.tokenType);
      commit('setIsSelfReactivated', data.isSelfReactivated);

      const { getTokenService } = await import('@/services/token');
      getTokenService().setTokens(data);

      if (data.twoFactorAuthorization?.enabled) {
        await dispatch('startMfaChallenge', {
          twoFactorAuthorization: data.twoFactorAuthorization,
          accessToken: data.accessToken
        });
      } else {
        commit('setTwoFactorAuthorization', null);
        commit('clearMfaChallenge');
        commit('setIsLoggedIn', true);
        commit('general/setSendAuthorizationHeader', true, { root: true });
        await dispatch('postLoginHydrate');
      }
    },

    async postLoginHydrate({ dispatch, state, getters, commit }) {
      if (!state.user && !getters.hasSecondFactorChallenge) {
        await dispatch('profile/dashboard/resetProfileStore', null, { root: true });
        await dispatch('fetchUser');
      }
      if (state.isBusy) {
        commit('setIsBusy', false);
      }
    },

    async login({ dispatch }, loginData) {
      try {
        const data = await this.$http.post('/v2/authorization', loginData);
        await dispatch('loginSuccess', data);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },

    async thirdPartyLogin({ dispatch }, loginData) {
      try {
        const data = await this.$http.post('/v2/authorization/third-party', loginData);
        await dispatch('loginSuccess', data);
      } catch (error) {
        console.error(error);
      }
    },

    async appleLogin({ dispatch }, loginData) {
      this.$log('Auth Store', 'apple login', 'D');
      try {
        const data = await this.$http.post('/v2/authorization/apple-sign-in', loginData);
        this.$log('Auth Store', 'apple login', 'S');
        await dispatch('loginSuccess', data);
      } catch (error) {
        this.$log('Auth Store', 'apple login', 'E');
        console.error(error);
      }
    },

    async fetchUser({ commit, dispatch, state, getters }, overrideMfa = false) {
      if (getters.hasSecondFactorChallenge && !overrideMfa) {
        return null;
      }

      if (pendingFetchUser) {
        return pendingFetchUser;
      }

      pendingFetchUser = this.$http
        .get('/v3/fe/web-user')
        .then(data => {
          commit('setUser', data);
          if (!state.isLoggedIn && state.accessToken && !getters.hasSecondFactorChallenge) {
            commit('setIsLoggedIn', true);
          }
          return data;
        })
        .catch(async error => {
          if (error?.status === 404) {
            await dispatch('reset');
          }
          console.error(error);
          return false;
        })
        .finally(() => {
          pendingFetchUser = null;
          if (state.isBusy) {
            commit('setIsBusy', false);
          }
        });

      return pendingFetchUser;
    },

    async logout({ dispatch }) {
      // Cancel in-flight requests first so late 401s don't trigger a second
      // logout cycle and pending data loads don't re-populate just-cleared
      // state. The DELETE below uses a fresh AbortController scoped per call.
      if (typeof this.$abortRequests === 'function') {
        this.$abortRequests();
      }

      try {
        await this.$http.delete('/v2/authorization/logout');
      } catch {
        // Logout API may fail (expired token, network error) — reset regardless.
        // 401 here is expected when the server-side session is already gone
        // (handled by tokenRefresh's `expectedFailure401` guard — no recursion).
      }

      await dispatch('reset');

      // Navigate via the platform-aware helper: router.replace on Capacitor
      // (mobile), `location.href` on web for a hard page reload that flushes
      // any component-local cached state. Test stubs $router so this is
      // observable.
      if (this.$router) {
        useLogoutRedirect(this.$router);
      }
    },

    async reset({ dispatch, commit }) {
      const { getTokenService } = await import('@/services/token');
      getTokenService().clearTokens();

      commit('setAccessToken', '');
      commit('setIsPasswordExpired', false);
      commit('setRefreshToken', '');
      commit('setSuccessAuthorizationReturnData', null);
      commit('setTokenType', 'bearer');
      commit('setTwoFactorAuthorization', null);
      commit('clearMfaChallenge');
      commit('setIsLoggedIn', false);
      commit('setUser', null);
      commit('general/setSendAuthorizationHeader', false, { root: true });
      commit('errorModal/setCode', undefined, { root: true });

      // Reset every namespaced PII-holding store. See plan §2 categorisation
      // table in notes/session-expiry-research.md for the full rationale —
      // additions here are mechanical, the table is authoritative.
      await dispatch('profile/dashboard/resetProfileStore', null, { root: true });
      await dispatch('forms/resetAll', null, { root: true });

      commit('soulodyApi/resetStore', null, { root: true });
      commit('vitalSigns/resetStore', null, { root: true });
      commit('hrbPartner/resetStore', null, { root: true });
      commit('aeskulab/resetStore', null, { root: true });

      commit('profile/accountDelete/resetStore', null, { root: true });
      commit('profile/authorization/resetStore', null, { root: true });
      commit('profile/evaluation/resetStore', null, { root: true });
      commit('profile/externalPartner/resetStore', null, { root: true });
      commit('profile/feedback/resetStore', null, { root: true });
      commit('profile/goPay/resetStore', null, { root: true });
      commit('profile/invitation/resetStore', null, { root: true });
      commit('profile/phoneVerification/resetStore', null, { root: true });
      commit('profile/reauthorization/resetStore', null, { root: true });
      commit('profile/registration/resetStore', null, { root: true });
      commit('profile/service/resetStore', null, { root: true });
      commit('profile/userAppData/resetStore', null, { root: true });
    },

    async refreshTokens({ dispatch }) {
      try {
        const data = await this.$http.post(
          '/v2/authorization/refresh-access-token?includeExpiresIn=1',
          {}
        );
        if (data?.accessToken) {
          await dispatch('loginSuccess', data);
        } else if (data?.expiresIn) {
          const { getTokenService } = await import('@/services/token');
          getTokenService().updateExpiry(data.expiresIn);
        } else {
          const { getTokenService } = await import('@/services/token');
          getTokenService().extendTokenExpiry();
        }
      } catch (error) {
        console.error(error);
      }
    }
  },
  namespaced: true
};
